/**
 * Admin / workspace API fetch with timeout + retry on transient 502/503/504 and network blips.
 */
import { fetchWithTimeout } from '../salon/utils/fetchWithTimeout';

const RETRY_STATUS = new Set([502, 503, 504]);

function sleep(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function isRetryableFetchError(err) {
  if (!err) return false;
  if (err.name === 'TimeoutError') return true;
  if (err.name === 'TypeError') return true;
  const msg = String(err.message || '');
  return /timed out|failed to fetch|network|load failed/i.test(msg);
}

/**
 * @param {RequestInfo | URL} url
 * @param {RequestInit & { timeoutMs?: number }} [init]
 * @param {{ attempts?: number }} [opts]
 */
export async function fetchAdminWithRetry(url, init = {}, opts = {}) {
  const attempts = Math.max(1, opts.attempts ?? 3);
  const timeoutMs = init.timeoutMs ?? 25000;
  let lastError = null;

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      const res = await fetchWithTimeout(url, { ...init, timeoutMs });
      if (RETRY_STATUS.has(res.status) && attempt < attempts - 1) {
        await sleep(700 + attempt * 900);
        continue;
      }
      return res;
    } catch (err) {
      lastError = err;
      if (isRetryableFetchError(err) && attempt < attempts - 1) {
        await sleep(700 + attempt * 900);
        continue;
      }
      throw err;
    }
  }

  throw lastError || new Error('Request failed');
}
